import { useEffect, useState } from "react";
import {
  formatWeekLabel,
  getWeekKey,
  loadAvailableWeeks,
  loadDuelRecords,
  loadHighscores,
  type DuelRecord,
  type HighscoreEntry,
} from "../game/storage";
import {
  CATEGORIES,
  DIFFICULTY_LABELS,
  type CategoryId,
  type Difficulty,
  type GameConfig,
  type SelectedCategory,
} from "../game/types";

interface MenuProps {
  onStart: (config: GameConfig) => void;
  onNavigateToKumon?: () => void;
}

const DIFFICULTY_ORDER: Difficulty[] = ["easy", "medium", "hard"];

export function Menu({ onStart, onNavigateToKumon }: MenuProps) {
  const [mode, setMode] = useState<"solo" | "duel">("solo");
  const [selectedCategory, setSelectedCategory] = useState<SelectedCategory>({
    categoryId: "add",
    difficulty: "easy",
  });
  const [p1Name, setP1Name] = useState("Player 1");
  const [p2Name, setP2Name] = useState("Player 2");
  const [highscores, setHighscores] = useState<HighscoreEntry[]>([]);
  const [duels, setDuels] = useState<DuelRecord[]>([]);

  // Leaderboard Modal State
  const [showModal, setShowModal] = useState(false);
  const [modalCat, setModalCat] = useState<CategoryId>("add");
  const [modalDiff, setModalDiff] = useState<Difficulty>("easy");
  const [modalWeek, setModalWeek] = useState<string>(getWeekKey());
  const [availableWeeks, setAvailableWeeks] = useState<string[]>([getWeekKey()]);
  const [modalScores, setModalScores] = useState<HighscoreEntry[]>([]);

  const [isFullscreen, setIsFullscreen] = useState(!!document.fullscreenElement);

  useEffect(() => {
    loadHighscores(selectedCategory.categoryId, selectedCategory.difficulty).then(setHighscores);
  }, [selectedCategory]);

  useEffect(() => {
    loadDuelRecords().then(setDuels);
    loadAvailableWeeks().then(setAvailableWeeks);
  }, []);

  useEffect(() => {
    if (showModal) {
      loadHighscores(modalCat, modalDiff, modalWeek).then(setModalScores);
      loadAvailableWeeks().then(setAvailableWeeks);
    }
  }, [showModal, modalCat, modalDiff, modalWeek]);

  useEffect(() => {
    const handleFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handleFsChange);
    return () => document.removeEventListener("fullscreenchange", handleFsChange);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
    }
  };

  const openLeaderboard = (catId?: CategoryId, diff?: Difficulty) => {
    setModalCat(catId ?? selectedCategory.categoryId);
    setModalDiff(diff ?? selectedCategory.difficulty);
    setModalWeek(getWeekKey());
    setShowModal(true);
  };

  const start = () => {
    onStart({
      mode,
      selectedCategory,
      player1Name: p1Name.trim() || "Player 1",
      player2Name: p2Name.trim() || "Player 2",
    });
  };

  const activeCatObj = CATEGORIES.find(c => c.id === selectedCategory.categoryId);
  const activeDiffObj = DIFFICULTY_LABELS[selectedCategory.difficulty];

  return (
    <div className="menu">
      <header className="menu-header">
        <div className="menu-title-group">
          <h1 className="title">
            MATH<span className="title-accent">WAR</span>
          </h1>
          <p className="subtitle">Race the clock. Beat the numbers.</p>
        </div>

        <div className="header-right-actions">
          {onNavigateToKumon && (
            <button
              type="button"
              className="nav-switch-btn kumon-switch"
              onClick={onNavigateToKumon}
              title="สลับไปยัง Kumon Mode (/kumon)"
            >
              📚 โหมด Kumon
            </button>
          )}
          <button
            type="button"
            className={`fullscreen-btn ${isFullscreen ? "active" : ""}`}
            onClick={toggleFullscreen}
            title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
          >
            <span className="fs-icon">{isFullscreen ? "🗗" : "⛶"}</span>
            <span className="fs-text">{isFullscreen ? "Exit" : "Fullscreen"}</span>
          </button>
        </div>
      </header>

      <div className="menu-body">
        <div className="menu-col-left">
          <section className="panel">
            <h2 className="panel-title">Mode</h2>
            <div className="mode-select">
              <button
                type="button"
                className={`mode-btn ${mode === "solo" ? "active" : ""}`}
                onClick={() => setMode("solo")}
              >
                <span className="mode-name">Solo</span>
                <span className="mode-desc">60s time attack · beat the highscore</span>
              </button>
              <button
                type="button"
                className={`mode-btn ${mode === "duel" ? "active" : ""}`}
                onClick={() => setMode("duel")}
              >
                <span className="mode-name">Duel</span>
                <span className="mode-desc">2 players · split screen · 60s</span>
              </button>
            </div>
          </section>

          <section className="panel">
            <h2 className="panel-title">Players</h2>
            <div className="name-row">
              <label className="name-field">
                <span className="name-tag">P1</span>
                <input
                  className="name-input"
                  value={p1Name}
                  onChange={e => setP1Name(e.target.value)}
                  maxLength={16}
                  placeholder="Player 1"
                />
              </label>
              {mode === "duel" && (
                <label className="name-field">
                  <span className="name-tag">P2</span>
                  <input
                    className="name-input"
                    value={p2Name}
                    onChange={e => setP2Name(e.target.value)}
                    maxLength={16}
                    placeholder="Player 2"
                  />
                </label>
              )}
            </div>
          </section>

          <div className="menu-action-buttons">
            <button
              type="button"
              className="start-btn"
              onClick={start}
            >
              {mode === "solo" ? "Start Solo Game" : "Start Duel"}
            </button>

            <button
              type="button"
              className="btn secondary leaderboard-btn"
              onClick={() => openLeaderboard()}
            >
              🏆 ดูคะแนน Top 10 (Leaderboard)
            </button>
          </div>
        </div>

        <div className="menu-col-right">
          <section className="panel">
            <h2 className="panel-title">Operations (Select One Mode & Difficulty)</h2>
            <div className="op-list">
              {CATEGORIES.map(cat => {
                const isCatActive = selectedCategory.categoryId === cat.id;
                return (
                  <div key={cat.id} className={`op-row op-${cat.id} ${isCatActive ? "active" : ""}`}>
                    <div className="op-toggle">
                      <span className="op-symbol">{cat.symbol}</span>
                      <span className="op-label">{cat.label}</span>
                    </div>
                    <div className="diff-select" role="group" aria-label={`${cat.label} difficulty`}>
                      {DIFFICULTY_ORDER.map(d => {
                        const diffInfo = DIFFICULTY_LABELS[d];
                        const isSelected = isCatActive && selectedCategory.difficulty === d;
                        return (
                          <button
                            key={d}
                            type="button"
                            className={`diff-btn ${isSelected ? "active" : ""}`}
                            onClick={() => setSelectedCategory({ categoryId: cat.id, difficulty: d })}
                          >
                            <span className="diff-emoji">{diffInfo.emoji}</span>
                            <span className="diff-name">{diffInfo.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="panel records">
            <div className="record-col">
              <div className="record-col-header">
                <h2 className="panel-title">
                  Top 10 Highscores ({activeCatObj?.symbol} · {activeDiffObj?.emoji})
                </h2>
                <button
                  type="button"
                  className="view-all-link"
                  onClick={() => openLeaderboard()}
                >
                  ดูทั้งหมด 🏆
                </button>
              </div>
              {highscores.length > 0 ? (
                <ol className="hs-table">
                  {highscores.slice(0, 10).map((h, i) => (
                    <li key={i} className="hs-row">
                      <span className="hs-rank">{i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}</span>
                      <span className="hs-name">{h.name}</span>
                      <span className="hs-score">{h.score} pts</span>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="no-records">ยังไม่มีคะแนนในโหมดนี้</p>
              )}
            </div>
            {duels.length > 0 && (
              <div className="record-col">
                <h2 className="panel-title">Recent Duels</h2>
                <ul className="duel-list">
                  {duels.slice(0, 10).map((d, i) => (
                    <li key={i} className="duel-row">
                      <span className="duel-winner">{d.winner ? `${d.winner} Won` : "Draw"}</span>
                      <span className="duel-score">
                        {d.p1.score} – {d.p2.score}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        </div>
      </div>

      {/* Top 10 Leaderboard Modal */}
      {showModal && (
        <div className="modal-backdrop" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">🏆 Top 10 Leaderboard (ประจำสัปดาห์ 🗓️)</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowModal(false)}
              >
                ✕
              </button>
            </div>

            {/* Week History Selector */}
            <div className="modal-week-bar">
              <label className="week-label-text">
                <span className="week-icon">🗓️</span>
                <span className="week-title">เลือกสัปดาห์:</span>
                <select
                  className="week-select-dropdown"
                  value={modalWeek}
                  onChange={(e) => setModalWeek(e.target.value)}
                >
                  {availableWeeks.map((w) => (
                    <option key={w} value={w}>
                      {formatWeekLabel(w)}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {/* Category Filter Tabs */}
            <div className="modal-category-tabs">
              {CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  type="button"
                  className={`modal-cat-tab ${modalCat === cat.id ? "active" : ""}`}
                  onClick={() => setModalCat(cat.id)}
                >
                  <span className="cat-symbol">{cat.symbol}</span>
                  <span className="cat-name">{cat.label}</span>
                </button>
              ))}
            </div>

            {/* Difficulty Filter Tabs */}
            <div className="modal-diff-tabs">
              {DIFFICULTY_ORDER.map(d => {
                const diffInfo = DIFFICULTY_LABELS[d];
                return (
                  <button
                    key={d}
                    type="button"
                    className={`modal-diff-tab ${modalDiff === d ? "active" : ""}`}
                    onClick={() => setModalDiff(d)}
                  >
                    <span>{diffInfo.emoji}</span>
                    <span>{diffInfo.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Scores Table */}
            <div className="modal-scores-container">
              <div className="modal-setting-header">
                <h3 className="modal-setting-label">
                  {CATEGORIES.find(c => c.id === modalCat)?.symbol} {CATEGORIES.find(c => c.id === modalCat)?.label} · {DIFFICULTY_LABELS[modalDiff]?.emoji} {DIFFICULTY_LABELS[modalDiff]?.label}
                </h3>
                <span className="modal-week-tag">{formatWeekLabel(modalWeek)}</span>
              </div>

              {modalScores.length > 0 ? (
                <ol className="hs-table modal-hs-table">
                  {modalScores.map((h, i) => (
                    <li key={i} className="hs-row modal-hs-row">
                      <span className="hs-rank">{i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}</span>
                      <span className="hs-name">{h.name}</span>
                      <span className="hs-details">(ถูก {h.correctCount ?? 0} / ผิด {h.wrongCount ?? 0})</span>
                      <span className="hs-score">{h.score} pts</span>
                    </li>
                  ))}
                </ol>
              ) : (
                <div className="no-records-box">
                  <p>ยังไม่มีสถิติ Top 10 ในโหมดนี้</p>
                  <span className="no-records-hint">ลองเล่นโหมดนี้แล้วทำสถิติเป็นคนแรก!</span>
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn"
                onClick={() => setShowModal(false)}
              >
                ปิดหน้าต่าง
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
